<template>
  <div class="flex flex-col items-end">
    <div
      ref="container"
      class="relative w-full"
    >
      <div class="relative">
        <div
          v-if="slots.icon"
          class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10"
          :class="[selectedIndex !== -1 ? 'text-blue-600' : 'text-gray-400']"
        >
          <slot name="icon" />
        </div>

        <input
          ref="inputRef"
          v-bind="$attrs"
          v-model="inputValue"
          :name="name"
          type="text"
          class="form-input focus:ring-transparent focus:ring-offset-transparent"
          :class="{
            '!border-b-gray-300 rounded-b-none': showList && !isPositionedAbove && (suggestions.length > 0 || (props.showEmptySuggestions && suggestions.length === 0)),
            '!border-t-gray-300 rounded-t-none': showList && isPositionedAbove && (suggestions.length > 0 || (props.showEmptySuggestions && suggestions.length === 0)),
            'pl-10': slots.icon,
            'text-blue-600': selectedIndex !== -1
          }"
          autocomplete="off"
          @input="emitValue"
          @focusin="focused"
          @keydown.tab="unfocused"
          @keyup="handleKeyUp"
        >
      </div>

      <Transition name="fade">
        <div
          v-if="showList"
          ref="suggestionList"
          class="fixed z-50 bg-white w-full overflow-y-auto border border-blue-500 flex flex-col max-h-40"
          :class="{
            'rounded-b-md border-t-0 shadow-lg': !isPositionedAbove && (suggestions.length > 0 || loadingSuggestions || (props.showEmptySuggestions && suggestions.length === 0)),
            'rounded-t-md border-b-0': isPositionedAbove && (suggestions.length > 0 || loadingSuggestions || (props.showEmptySuggestions && suggestions.length === 0)),
            invisible: !isInputVisible
          }"
          :style="dropdownStyle"
        >
          <Transition
            name="fade"
            mode="out-in"
          >
            <div v-if="suggestions.length && !loadingSuggestions">
              <div
                v-for="(suggestion, i) in suggestions"
                :key="`${getValueForSuggestion(suggestion)}-${i}`"
                class="px-3 py-2 cursor-pointer text-sm text-gray-900 hover:bg-gray-50 transition-colors duration-150"
                tabindex="0"
                :class="{ 'bg-blue-50 text-blue-900 border-l-2 border-blue-500': i === activeItem }"
                :data-test="`suggestion-${i}`"
                @click.prevent="selectSuggestion(i, suggestion)"
                @mouseenter="activeItem = i"
              >
                <p class="truncate">
                  {{ getLabelForSuggestion(suggestion) }}
                </p>
              </div>
            </div>

            <div
              v-else-if="loadingSuggestions"
              class="flex items-center justify-center py-2 px-3"
            >
              <div class="flex items-center space-x-2 text-gray-500">
                <AppSpinner spinner-class="w-4 h-4" />

                <span class="text-sm">{{ $t('TypeAhead.loading') }}</span>
              </div>
            </div>

            <div v-else-if="showEmptySuggestions">
              <div class="flex items-center justify-center py-2 px-3">
                <p class="text-sm text-gray-500">
                  <slot name="emptyText">
                    No suggestions found.
                  </slot>
                </p>
              </div>
            </div>
          </Transition>
        </div>
      </Transition>
    </div>

    <span
      v-if="error"
      class="text-red-600 text-sm mr-auto"
    >{{ error }}</span>
  </div>
</template>

<script setup lang="ts" generic="T extends { label: string; value: unknown } | string">
import { onClickOutside, useDebounceFn } from '@vueuse/core';
import { computed, nextTick, onMounted, onUnmounted, reactive, ref, useSlots, useTemplateRef, watch } from 'vue';

const props = withDefaults(
  defineProps<{
    name: string;
    // modelValue: string | number | null | undefined;
    error?: string;
    suggestionCallback: () =>
      | Promise<string[] | T[]>
      | (string[] | T[]);
    initialSuggestions?: () =>
      | Promise<string[] | T[]>
      | (string[] | T[]);
    showEmptySuggestions?: boolean;
    scrollableContainer?: HTMLElement;
  }>(),
  {
    error: undefined,
    initialSuggestions: undefined,
    showEmptySuggestions: true,
    scrollableContainer: undefined,
    /* eslint-disable no-null/no-null */
    // modelValue: null,
  },
);

const inputValue = defineModel();

const emit = defineEmits<{
  // (e: 'update:modelValue', value: string | number | null | undefined);
  (e: 'valueSelected', index: number, value: string | number | null | undefined);
  (e: 'valueWasSelected', data: { index: number; value: T });
}>();

const suggestions = ref([]);
const activeItem = ref(-1);
const inputFocused = ref(false);
const loadingSuggestions = ref(false);
const container = ref<HTMLDivElement>();
const suggestionList = ref<HTMLDivElement>();
const inputRef = useTemplateRef('inputRef');
const selectedIndex = ref(-1);
const isPositionedAbove = ref(false);
const isInputVisible = ref(true);
const dropdownStyle = reactive({ top: '0px', left: '0px', maxWidth: '0px' });

const calculateDropdownPosition = () => {
  if(!inputRef.value) return;
  if(!suggestionList.value) return;

  const rect = inputRef.value.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;
  const dropdownHeight = suggestionList.value.offsetHeight;
  const spaceBelow = viewportHeight - rect.bottom;
  const spaceAbove = rect.top;
  // Find the closest scrollable container
  const closestScrollableContainer = props.scrollableContainer;
  // Check if input is visible within its container bounds
  let isOffScreen = false;

  if(closestScrollableContainer) {
    // Check visibility within the scrollable container
    const containerRect = closestScrollableContainer.getBoundingClientRect();
    const containerClientHeight = closestScrollableContainer.clientHeight;
    const containerClientWidth = closestScrollableContainer.clientWidth;
    // Calculate the visible bounds of the container
    const containerVisibleTop = containerRect.top;
    const containerVisibleBottom = containerRect.top + containerClientHeight;
    const containerVisibleLeft = containerRect.left;
    const containerVisibleRight = containerRect.left + containerClientWidth;

    // Check if input is outside the visible container bounds
    isOffScreen = rect.bottom < containerVisibleTop ||
      rect.top > containerVisibleBottom ||
      rect.right < containerVisibleLeft ||
      rect.left > containerVisibleRight;
  } else {
    // No scrollable container found, check viewport bounds
    isOffScreen = rect.bottom < 0 ||
      rect.top > viewportHeight ||
      rect.right < 0 ||
      rect.left > viewportWidth;
  }

  isInputVisible.value = !isOffScreen;

  // If input is off-screen, don't calculate position
  if(isOffScreen) {
    return;
  }

  // Check if dropdown would overflow below, and if there's more space above
  if(spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
    // Position above the input
    isPositionedAbove.value = true;
    // Ensure dropdown doesn't go above viewport
    const topPosition = Math.max(0, rect.top - dropdownHeight);

    dropdownStyle.top = `${topPosition}px`;
  } else {
    // Position below the input (default)
    isPositionedAbove.value = false;
    dropdownStyle.top = `${rect.bottom}px`;
  }

  dropdownStyle.left = `${rect.left}px`;
  dropdownStyle.maxWidth = `${rect.width}px`;
};

let observer = undefined;

watch(suggestionList, (newValue, oldValue) => {
  if(!newValue) {
    observer?.disconnect();
    observer = undefined;

    return;
  }

  // Create an observer instance linked to the callback function
  observer = new ResizeObserver((entries) => {
    calculateDropdownPosition();
  });

  // Start observing the target node for configured mutations
  observer.observe(newValue);
}, { immediate: true });

const handleScroll = () => {
  if(showList.value) {
    calculateDropdownPosition();
  }
};

const findScrollableParents = (element: HTMLElement | null): HTMLElement[] => {
  const scrollableParents: HTMLElement[] = [];
  let current = element?.parentElement;

  while(current) {
    const overflow = window.getComputedStyle(current).getPropertyValue('overflow-y');

    if(overflow === 'auto' || overflow === 'scroll') {
      scrollableParents.push(current);
    }

    current = current.parentElement;
  }

  return scrollableParents;
};

let scrollListeners: { element: HTMLElement | Window; handler: () => void }[] = [];

const attachScrollListeners = () => {
  // Remove existing listeners first
  detachScrollListeners();

  // Add window scroll listener
  const windowHandler = handleScroll;

  window.addEventListener('scroll', windowHandler, true);
  scrollListeners.push({ element: window, handler: windowHandler });

  // Add scroll listeners to scrollable parent elements
  if(inputRef.value) {
    const scrollableParents = findScrollableParents(inputRef.value);

    scrollableParents.forEach((parent) => {
      const handler = handleScroll;

      parent.addEventListener('scroll', handler, true);
      scrollListeners.push({ element: parent, handler });
    });
  }
};

const detachScrollListeners = () => {
  scrollListeners.forEach(({ element, handler }) => {
    if(element === window) {
      window.removeEventListener('scroll', handler, true);
    } else {
      element.removeEventListener('scroll', handler, true);
    }
  });

  scrollListeners = [];
};

const selectNext = () => {
  activeItem.value = (activeItem.value + 1) % suggestions.value.length;

  if(suggestionList.value.children[activeItem.value]) {
    suggestionList.value.children[activeItem.value].scrollIntoView();
  }
};

const selectPrev = () => {
  activeItem.value = (activeItem.value - 1) < 0 ? suggestions.value.length - 1 : activeItem.value - 1;

  if(suggestionList.value.children[activeItem.value]) {
    suggestionList.value.children[activeItem.value].scrollIntoView();
  }
};

const selectCurrent = () => {
  // emit('update:modelValue', suggestions.value[activeItem.value]);
  inputValue.value = getLabelForSuggestion(suggestions.value[activeItem.value]);

  nextTick(() => {
    selectedIndex.value = activeItem.value;
  });

  emit('valueSelected', activeItem.value, suggestions.value[activeItem.value]);
  emit('valueWasSelected', { index: activeItem.value, value: suggestions.value[activeItem.value] });
  inputFocused.value = false;
  suggestions.value = [];
};

const handleKeyUp = (event: KeyboardEvent) => {
  inputFocused.value = true;

  switch (event.keyCode) {
    case 40: // down arrow
      selectNext();
      break;

    case 38: // up arrow
      selectPrev();
      break;

    case 13: // enter
      selectCurrent();
      break;
  }
};

const getSuggestions = useDebounceFn(async () => {
  loadingSuggestions.value = true;

  suggestions.value = await props.suggestionCallback();

  loadingSuggestions.value = false;

  activeItem.value = -1;
}, 500);

const getInitialSuggestions = async () => {
  loadingSuggestions.value = true;

  suggestions.value = await props.initialSuggestions();

  loadingSuggestions.value = false;

  activeItem.value = -1;
};

const emitValue = (e: Event) => {
  // emit('update:modelValue', (e.target as HTMLInputElement).value);
  inputValue.value = (e.target as HTMLInputElement).value;

  getSuggestions();
};

const selectSuggestion = (index: number, value: T) => {
  // emit('update:modelValue', value);
  inputValue.value = getLabelForSuggestion(value);

  nextTick(() => {
    selectedIndex.value = index;
  });

  emit('valueSelected', index, getValueForSuggestion(value) as string);
  emit('valueWasSelected', { index, value });
  inputFocused.value = false;
};

onClickOutside(container, () => {
  inputFocused.value = false;
  detachScrollListeners();
});

const focused = () => {
  inputFocused.value = true;
  // calculateDropdownPosition();
  attachScrollListeners();

  if(inputValue.value) {
    getSuggestions();
  } else if(props.initialSuggestions) {
    getInitialSuggestions();
  }
};

const unfocused = (_e: KeyboardEvent) => {
  inputFocused.value = false;
  detachScrollListeners();
};

watch(() => inputValue.value, (newValue, oldValue) => {
  if(!oldValue && newValue) {
    suggestions.value = [];
    activeItem.value = -1;
  }

  selectedIndex.value = -1;
});

const showList = computed(() => {
  return (inputValue.value || props.initialSuggestions) && inputFocused.value &&
    (loadingSuggestions.value || suggestions.value.length > 0 ||
      (props.showEmptySuggestions && suggestions.value.length === 0));
});

watch(showList, (isVisible) => {
  if(isVisible) {
    nextTick(() => {
      calculateDropdownPosition();
    });

    attachScrollListeners();
  } else {
    detachScrollListeners();
  }
});

watch(() => suggestions.value.length, () => {
  if(showList.value) {
    nextTick(() => {
      calculateDropdownPosition();
    });
  }
});

onUnmounted(() => {
  detachScrollListeners();
});

const slots = useSlots();

const setSelectedIndex = (index: number) => {
  selectedIndex.value = index;
};

const getValueForSuggestion = (suggestion: string | { label: string; value: unknown }) => {
  if(typeof suggestion === 'string') {
    return suggestion;
  }

  return suggestion.value;
};

const getLabelForSuggestion = (suggestion: string | { label: string; value: unknown }) => {
  if(typeof suggestion === 'string') {
    return suggestion;
  }

  return suggestion.label;
};

defineOptions({
  inheritAttrs: false,
});

defineExpose({
  setSelectedIndex,
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
